import { useCallback, useEffect, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type OnNodesChange,
  type OnEdgesChange,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { TriggerNode } from './nodes/trigger-node';
import { ConditionNode } from './nodes/condition-node';
import { ActionNode } from './nodes/action-node';
import { EndNode } from './nodes/end-node';
import { DefaultActionNode } from './nodes/default-action-node';
import { AddBranchNode } from './nodes/add-branch-node';
import { ChannelEdge } from './edges/channel-edge';
import { BypassEdge } from './edges/bypass-edge';
import { useFlowLayout } from './hooks/use-flow-layout';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useUIStore } from '@/stores/ui-store';
import type { MMMCard, FieldMappingDocument } from '@/types/field-mapping-flow';

// ── Position helpers ──
type PositionMap = Record<string, { x: number; y: number }>;

/** Collect all saved nodePositions from every branch into a single map */
function collectSavedPositions(
  fieldMappings: Record<string, FieldMappingDocument>
): PositionMap {
  const all: PositionMap = {};
  for (const mapping of Object.values(fieldMappings)) {
    if (mapping.nodePositions) {
      Object.assign(all, mapping.nodePositions);
    }
  }
  return all;
}

/**
 * Nodes whose Y position is dynamically computed based on the heights of other
 * nodes (card counts, etc.). Saved positions must NOT override their computed
 * layout positions, otherwise they go stale when content changes.
 */
const DYNAMIC_POSITION_NODES = new Set(['end', 'default-action']);

/** Apply saved positions over computed layout nodes (skips dynamic nodes) */
function applyPositions(nodes: Node[], saved: PositionMap): Node[] {
  return nodes.map((n) => {
    if (DYNAMIC_POSITION_NODES.has(n.id)) return n; // always use computed position
    const pos = saved[n.id];
    return pos ? { ...n, position: pos } : n;
  });
}

/**
 * Determine which branch doc owns a given flow node.
 * - condition-{docId}, action-{docId} → that branch
 * - trigger, end, add-branch → default branch
 */
function getOwnerDocId(
  nodeId: string,
  fieldMappings: Record<string, FieldMappingDocument>
): string | null {
  const match = nodeId.match(/^(?:condition|action)-(.+)$/);
  if (match) return match[1];

  // Global nodes (trigger, end, add-branch, default-action) → default branch
  const defaultEntry = Object.entries(fieldMappings).find(
    ([, m]) => m.filterValue === '---default'
  );
  return defaultEntry ? defaultEntry[0] : null;
}

// Register custom node types
const nodeTypes = {
  trigger: TriggerNode,
  condition: ConditionNode,
  action: ActionNode,
  end: EndNode,
  defaultAction: DefaultActionNode,
  addBranch: AddBranchNode,
};

// Register custom edge types
const edgeTypes = {
  channel: ChannelEdge,
  bypass: BypassEdge,
};

function makeRandomId(length = 7): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Inner component that uses useReactFlow() (must be inside ReactFlowProvider).
 * Handles node/edge sync with proper useEffect-based approach.
 */
function FlowCanvasInner() {
  const { nodes: layoutNodes, edges: layoutEdges } = useFlowLayout();
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const updateNodePositions = useFlowBuilderStore((s) => s.updateNodePositions);
  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutEdges);
  const { fitView } = useReactFlow();
  const isDark = useUIStore((s) => s.theme) === 'dark';

  // Build a signature that captures both structure (node IDs) and layout-affecting
  // data (card counts). When cards are added/removed the End node and Default Actions
  // node need to reposition, so we must treat card-count changes as structural.
  const computeLayoutSignature = useCallback((lnodes: Node[]): string => {
    return lnodes
      .map((n) => {
        const data = n.data as Record<string, unknown>;
        const cards = data?.cards;
        const cardCount = Array.isArray(cards) ? cards.length : 0;
        return `${n.id}:${cardCount}`;
      })
      .join(',');
  }, []);

  // Track previous layout signature to detect structural / layout-affecting changes
  const prevSignatureRef = useRef<string>(
    computeLayoutSignature(layoutNodes)
  );
  // Force position restore on first mount (prevSignatureRef matches initial render,
  // so isLayoutChange would be false without this flag)
  const isFirstMount = useRef(true);

  useEffect(() => {
    const newSignature = computeLayoutSignature(layoutNodes);
    const isLayoutChange = newSignature !== prevSignatureRef.current;

    if (isLayoutChange || isFirstMount.current) {
      const wasFirstMount = isFirstMount.current;
      isFirstMount.current = false;

      // Apply saved positions from branch documents over computed layout
      const saved = collectSavedPositions(fieldMappings);
      const merged = applyPositions(layoutNodes, saved);

      setNodes(merged);
      setEdges(layoutEdges);
      prevSignatureRef.current = newSignature;

      // Fit view after structural / layout changes.
      // First mount: animate zoom-out for "expand from center" feel.
      // Subsequent layout changes: instant refit (no animation).
      requestAnimationFrame(() => {
        fitView({ padding: 0.3, maxZoom: 1.2, ...(wasFirstMount ? { duration: 350 } : {}) });
      });
    } else {
      // Data-only change (card added, name changed, etc.) —
      // update only the data property to preserve user-dragged positions
      setNodes((currentNodes: Node[]) =>
        currentNodes.map((node) => {
          const layoutNode = layoutNodes.find((ln) => ln.id === node.id);
          if (layoutNode) {
            return { ...node, data: layoutNode.data };
          }
          return node;
        })
      );
      setEdges(layoutEdges);
    }
  }, [layoutNodes, layoutEdges, setNodes, setEdges, fitView, fieldMappings, computeLayoutSignature]);

  const handleNodesChange: OnNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
    },
    [onNodesChange]
  );

  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes);
    },
    [onEdgesChange]
  );

  // Save node positions to the store when user finishes dragging (marks branch dirty)
  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, _node: Node, allNodes: Node[]) => {
      const store = useFlowBuilderStore.getState();
      const mappings = store.fieldMappings;

      // Group moved positions by owning branch (skip dynamic-position nodes
      // like 'end' and 'default-action' whose positions are computed from content)
      const byBranch: Record<string, PositionMap> = {};
      for (const n of allNodes) {
        if (DYNAMIC_POSITION_NODES.has(n.id)) continue;
        const docId = getOwnerDocId(n.id, mappings);
        if (!docId) continue;
        if (!byBranch[docId]) byBranch[docId] = {};
        byBranch[docId][n.id] = { x: n.position.x, y: n.position.y };
      }

      // Update each branch's nodePositions in the store (marks dirty)
      for (const [docId, positions] of Object.entries(byBranch)) {
        updateNodePositions(docId, positions);
      }
    },
    [updateNodePositions]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    const moduleDataStr = e.dataTransfer.getData('application/flow-module');
    if (!moduleDataStr) return;

    try {
      const { moduleValue, moduleLabel } = JSON.parse(moduleDataStr) as {
        moduleValue: string;
        moduleLabel: string;
      };

      const store = useFlowBuilderStore.getState();
      const targetBranchId = store.selectedBranchId;
      if (!targetBranchId) return;

      // Prevent duplicate modules on the same branch
      const mapping = store.fieldMappings[targetBranchId];
      if (mapping?.fields && mapping.isMMM) {
        const existingCards = Object.values(mapping.fields).filter(
          (val): val is MMMCard =>
            typeof val === 'object' && val !== null && 'cardId' in val
        );
        const alreadyHasModule = existingCards.some(
          (c) => c.saasFieldModuleName === moduleValue
        );
        if (alreadyHasModule) return;
      }

      const cardId = makeRandomId(7);
      const newCard: MMMCard = {
        cardId,
        saasFieldModuleName: moduleValue,
        displayLabel: moduleLabel,
        orderOf: 0,
        fields: {},
        fields_type: {},
        ct: Date.now(),
      };

      store.addCard(targetBranchId, newCard);
    } catch {
      // ignore parse errors
    }
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={handleNodesChange}
      onEdgesChange={handleEdgesChange}
      onNodeDragStop={handleNodeDragStop}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      fitViewOptions={{
        padding: 0.3,
        maxZoom: 1.2,
      }}
      minZoom={0.2}
      maxZoom={2}
      defaultEdgeOptions={{
        type: 'smoothstep',
        style: {
          stroke: isDark ? '#475569' : '#cbd5e1',
          strokeWidth: 1.5,
        },
      }}
      proOptions={{
        hideAttribution: true,
      }}
      // Drag-and-drop from sidebar modules
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      // Disable connecting nodes manually
      nodesConnectable={false}
      // Disable deleting nodes with backspace
      deleteKeyCode={null}
      // Allow internal drag-to-reorder inside nodes (elements with "nodrag" class won't trigger node drag)
      noDragClassName="nodrag"
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={20}
        size={1}
        color={isDark ? '#334155' : undefined}
      />
      <Controls
        showInteractive={false}
        className="!bg-card !border-border !shadow-md [&>button]:!bg-card [&>button]:!border-border [&>button]:!fill-foreground [&>button:hover]:!bg-muted"
      />
      <MiniMap
        nodeStrokeColor="var(--color-border)"
        nodeColor="var(--color-muted)"
        maskColor={isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.08)'}
        className="!bg-card !border-border !shadow-md"
      />
    </ReactFlow>
  );
}

/**
 * FlowCanvas — wraps FlowCanvasInner in ReactFlowProvider so that
 * useReactFlow() works inside the inner component.
 */
export function FlowCanvas() {
  return (
    <ReactFlowProvider>
      <div className="h-full w-full animate-flow-canvas-enter">
        <FlowCanvasInner />
      </div>
    </ReactFlowProvider>
  );
}
